import type {
  ApiErrorResponse,
  CreateProfileResponse,
  DeleteProfileResponse,
  PublicProfile,
  ResultsResponse,
  SubmitResponse,
} from "../shared/types";
import type { RawAnswer } from "../shared/scoring";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(response: ApiErrorResponse, status: number) {
    super(response.message);
    this.name = "ApiError";
    this.code = response.code;
    this.status = status;
  }
}

async function readResponse<T>(response: Response): Promise<T> {
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const fallback: ApiErrorResponse = { code: "REQUEST_FAILED", message: "网络开小差了，请稍后重试" };
    const detail = data && typeof data === "object" ? { ...fallback, ...(data as Partial<ApiErrorResponse>) } : fallback;
    throw new ApiError(detail as ApiErrorResponse, response.status);
  }
  return data as T;
}

export async function createProfile(displayName: string): Promise<CreateProfileResponse> {
  return readResponse<CreateProfileResponse>(await fetch("/api/profiles", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ displayName }),
  }));
}

export async function getPublicProfile(slug: string): Promise<PublicProfile> {
  return readResponse<PublicProfile>(await fetch(`/api/profiles/${encodeURIComponent(slug)}`, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  }));
}

export async function submitResponse(
  slug: string,
  friendNickname: string,
  respondentToken: string,
  answers: readonly RawAnswer[],
): Promise<SubmitResponse> {
  return readResponse<SubmitResponse>(await fetch(`/api/profiles/${encodeURIComponent(slug)}/responses`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ friendNickname, respondentToken, privacyConsent: true, answers }),
  }));
}

export async function getResults(slug: string, ownerToken: string): Promise<ResultsResponse> {
  return readResponse<ResultsResponse>(await fetch(`/api/profiles/${encodeURIComponent(slug)}/results`, {
    headers: { Accept: "application/json", Authorization: `Bearer ${ownerToken}` },
    cache: "no-store",
  }));
}

export async function deleteProfile(slug: string, ownerToken: string): Promise<DeleteProfileResponse> {
  return readResponse<DeleteProfileResponse>(await fetch(`/api/profiles/${encodeURIComponent(slug)}`, {
    method: "DELETE",
    headers: { Accept: "application/json", Authorization: `Bearer ${ownerToken}` },
  }));
}
